import type { LocationMap } from "@/components/PlacePicker/types";

/**
 * Persisted `location_map` values are not uniform. Rows written before the
 * PlacePicker consolidation can be any of:
 *
 *   - `{ description, place_id, label }` (the current contract)
 *   - a raw legacy Google prediction, with `structured_formatting`
 *   - a raw react-select option, with `label` and a nested `value`
 *   - a bare string
 *
 * Reads therefore stay tolerant even though every write is now uniform.
 */
interface GoogleLocationLike {
  label?: string;
  description?: string;
  place_id?: string;
  placeId?: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
}

export const extractGoogleLocationLabel = (
  googleLocation: GoogleLocationLike | null | undefined
): string | null => {
  if (!googleLocation) return null;
  if (googleLocation.label) return googleLocation.label;
  if (googleLocation.description) return googleLocation.description;
  if (googleLocation.structured_formatting) {
    const { main_text, secondary_text } = googleLocation.structured_formatting;
    if (main_text && secondary_text) return `${main_text}, ${secondary_text}`;
    if (main_text) return main_text;
  }
  return null;
};

/**
 * Coerces any persisted shape into the current contract so PlacePicker receives
 * a consistent `value`. Returns null when there is nothing displayable.
 */
export const normalizeLocationMap = (value: unknown): LocationMap | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return { description: value, label: value, place_id: "" };
  }

  if (typeof value !== "object") {
    return null;
  }

  const candidate = value as GoogleLocationLike;

  // description before label, matching the behaviour this replaces in
  // HubSettings. extractGoogleLocationLabel keeps its own label-first order
  // because display call sites already depend on it.
  const description =
    candidate.description || extractGoogleLocationLabel(candidate) || "";

  if (!description) {
    return null;
  }

  return {
    description,
    label: candidate.label || description,
    place_id: candidate.place_id || candidate.placeId || "",
  };
};
